/* lib/pricing.js — THE shared pricing engine + per-product allow-list/config
 * helpers, ported VERBATIM from the prototype (HTML 56446-56501, 54723-54759,
 * 53800, 53820-53829, 52854-52855).
 *
 * SIGNATURE CONVENTION (the only translation from the prototype): functions
 * that read the master libraries (PRICE_GROUPS/HANDLES/GOLA/PLINTH_MATERIALS/
 * COUNTERTOPS/EQUIPMENT/APPLIANCES/LIGHTING) take the store `blob` as their
 * FIRST argument instead of module globals. Pure per-unit functions keep the
 * prototype signatures unchanged.
 *
 * Preserved quirks (do NOT "fix" — cat-arch.md §6 / cat-data.md gotchas):
 *   • priceCabinet passes the door COLOUR object as computeDoorCost's style
 *     arg, so DOOR_STYLES.addM2 is effectively unused (0) in the engine.
 *   • opts id encoding is asymmetric: carcass/gola = array INDEX, groups =
 *     price-group NUMBER (g.g), plinth = pmat KEY string (fallback index),
 *     lighting/fabric/leather = string ids. Must stay byte-compatible with
 *     the /studio engine's lhcOptFilter.
 *   • optToggle collapses a full selection back to null — EXCEPT lighting.
 *   • cfgOf / prodOpts lazily create u.cfg / u.opts by MUTATION (call them
 *     inside store.mutate() when writing; normalize() pre-creates cfg so
 *     render-path reads never mutate). */
import { formatMoneyShort } from "../../lib/money";
import { resolveIdx as _uidResolveIdx, tokFor as _uidTokFor } from "../../configurator/engine/catalogIds";
import {
  unitHasFront, unitNeedsPlinth, unitHasWorktop, unitHasSink, unitWidthCm,
  unitFaceDims, cfgDimsM, lightingTypesFor, unitHasDrawers, unitIsTall,
} from "./units";

/* ── formatting (prototype 52854-52855) ─────────────────────────────────── */
export function money(n) { return formatMoneyShort(n); }   // ONE locale platform-wide (2026-08-18)
export function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
  });
}

/* ── component costs (prototype 56446-56473) ────────────────────────────── */
export function computeDoorCost(u, g, st) {
  if (!unitHasFront(u)) return null;
  var d = unitFaceDims(u); if (!d || !g) return null;
  var rate = (g.price || 0) + ((st && st.addM2) || 0);
  var area = Math.round((d.w / 100) * (d.h / 100) * 1000) / 1000;
  var cost = area * rate, os = false, ov = g.oversize;
  if (ov && ov.overMm && Math.max(d.w, d.h) * 10 > ov.overMm) { cost = cost * (1 + (ov.addPct || 0) / 100); os = true; }
  return { area: area, group: g, style: st || { name: "" }, rate: rate, cost: Math.round(cost * 100) / 100, oversize: os };
}

export function computePlinthCost(u, mat, frontRate) {
  if (!unitNeedsPlinth(u)) return null;
  var w = unitWidthCm(u); if (w == null || !mat) return null;
  if (mat.key === "door") {                                  // front-material plinth: strip area x that price group's rate
    var H = 0.10;   // the platform plinth height (LHC_RULES.PLINTH_H twin — engines stay decoupled by design)
    var areaM2 = Math.round((w / 100) * H * 1000) / 1000, rate = (frontRate != null ? frontRate : 0);
    return { width: w, mat: mat, basis: "perM2", rate: rate, areaM2: areaM2, cost: Math.round(areaM2 * rate * 100) / 100 };
  }
  return { width: w, mat: mat, basis: "perM", rate: (mat.price || 0), cost: Math.round((w / 100) * (mat.price || 0) * 100) / 100 };
}

export function computeGolaCost(u, gola) {
  if (!unitHasFront(u)) return null;
  if (!gola) return null;
  var w = unitWidthCm(u); if (w == null) return null;
  var lenM = Math.round((w / 100) * 1000) / 1000, runs = 1, rate = (gola.price || 0);
  return { lengthM: lenM, runs: runs, rate: rate, gola: gola, cost: Math.round(lenM * runs * rate * 100) / 100 };
}

/* ── lighting (prototype 53820-53829; LIGHTING = blob.lighting) ─────────── */
function lgRound(n) { return Math.round((n || 0) * 100) / 100; }
export function drawerSizeFor(lighting, u) {
  var w = unitWidthCm(u); var best = null;
  (((lighting || {}).drawer || {}).sizes || []).forEach(function (s) {
    if (best === null || Math.abs(s.cm - w) < Math.abs(best.cm - w)) best = s;
  });
  return best;
}
export function computeLightingCost(blob, u, type, sensor) {
  if (!u) return null;
  var L = (blob && blob.lighting) || {};
  var tr = (L.transformer || 0);
  if (type === "drawer") {
    var s = drawerSizeFor(L, u); if (!s) return null;
    return { type: "drawer", label: "Drawer Lighting " + s.cm + "cm + transformer", sensor: true, cost: lgRound(s.price + tr) };
  }
  if (type === "vertical") {
    var h = cfgDimsM(u).height || 0;
    var b = ((L.vertical && L.vertical.pricePerM) || 0) * h * 2 + tr;
    return { type: "vertical", label: "Vertical Lighting (pair) + transformer", sensor: true, cost: lgRound(b) };
  }
  if (type === "horizontal") {
    var w = (unitWidthCm(u) || 0) / 100;
    var c = ((L.horizontal && L.horizontal.pricePerM) || 0) * w + tr;
    return { type: "horizontal", label: "Horizontal Lighting + transformer", sensor: true, cost: lgRound(c) };
  }
  return null;
}

/* ── per-row config + allow-lists (prototype 54723-54749; v4.56 field set) ─ */
export const CFG_DEFAULTS = { carc: -1, grp: -1, colour: -1, style: 0, sys: "none", handle: -1, gola: -1, plinth: -1, plinthColor: -1, lighting: 0, lightType: "none", lightSensor: false, counter: -1, appl: -1, sink: -1, shelf: -1, drawer: -1, organizer: -1, worktop: -1, backsplash: -1, doorOpening: "left", bin: -1, corner: -1, pullout: -1, column: -1, hinge: -1, sidefront: -1, sideworktop: -1, eqSinks: -1, eqTaps: -1, eqHobs: -1, eqOvens: -1, eqHoods: -1, eqFridges: -1, eqDishwashers: -1 };

/* ── MODULE COMPONENTS — the ONE table shared by the options panel, the
 * price breakdown and the allow-list clamp (2026-07-31). Every component
 * family the Products table shows an allow-list column for is configurable
 * on the module and prices its own line. `cat` is the library the items come
 * from (blob.equipment[cat], or the special groups/worktops/handles libs);
 * `tab` groups it onto the same Material Library page the column comes from. */
export const MODULE_COMPONENTS = [
  { field: "shelf", cat: "shelves", label: "Internal shelves", tab: "carcasses" },
  { field: "drawer", cat: "drawers", label: "Drawers", tab: "carcasses" },
  { field: "organizer", cat: "organizers", label: "Drawer organizers", tab: "carcasses" },
  { field: "bin", cat: "bins", label: "Bins", tab: "carcasses" },
  { field: "corner", cat: "corners", label: "Corners", tab: "carcasses" },
  { field: "pullout", cat: "pullouts", label: "Pull-outs", tab: "carcasses" },
  { field: "column", cat: "columns", label: "Columns", tab: "carcasses" },
  { field: "sidefront", cat: "sidefront", label: "Side cover · front", tab: "carcasses" },
  { field: "sideworktop", cat: "sideworktop", label: "Side cover · worktop", tab: "carcasses" },
  { field: "handle", cat: "handles", label: "Handles", tab: "handles" },
  { field: "hinge", cat: "hinges", label: "Hinges", tab: "handles" },
];
/* Appliance types the Products "Equipment" allow-list gates; each type then
 * offers its own models from that Equipment Library category. */
export const EQUIPMENT_TYPES = [
  { field: "eqSinks", cat: "sinks", label: "Sinks" },
  { field: "eqTaps", cat: "taps", label: "Taps" },
  { field: "eqHobs", cat: "hobs", label: "Hobs" },
  { field: "eqOvens", cat: "ovens", label: "Ovens" },
  { field: "eqHoods", cat: "hoods", label: "Hoods" },
  { field: "eqFridges", cat: "fridges", label: "Fridges" },
  { field: "eqDishwashers", cat: "dishwashers", label: "Dishwashers" },
];
export function cfgOf(u) {
  if (!u.cfg) u.cfg = { ...CFG_DEFAULTS };
  else for (const k in CFG_DEFAULTS) { if (u.cfg[k] === undefined) u.cfg[k] = CFG_DEFAULTS[k]; }
  return u.cfg;
}
export function prodOpts(u) { if (!u.opts) u.opts = {}; return u.opts; }
/* 2026-07-22 user rule: "NONE means NO RESTRICTION". An EMPTY allow-list is the
 * technician's "None" state — it must behave exactly like null (unrestricted),
 * never like "nothing allowed". Only a NON-empty list restricts. */
/* STANDARD handle placements (2026-08-11 "placement needs to be top bottom
 * center"): every handle offers these three out of the box — Top / Center /
 * Bottom on the front (doors: near the opening edge; drawers: centred).
 * Authoring ANY custom position on a handle REPLACES the standard set. */
export var DEFAULT_HANDLE_PLACEMENTS = [
  { id: "pl-top", label: "Top", v: "top", vMm: 50, h: "edge", hMm: 50, rot: 0 },
  { id: "pl-center", label: "Center", v: "middle", vMm: 0, h: "edge", hMm: 50, rot: 0 },
  { id: "pl-bottom", label: "Bottom", v: "bottom", vMm: 50, h: "edge", hMm: 50, rot: 0 },
];
export function handlePlacementsOf(h) {
  var a = h && Array.isArray(h.placements) ? h.placements.filter(function (pl) { return pl && pl.id; }) : [];
  return a.length ? a : DEFAULT_HANDLE_PLACEMENTS;
}

/* ── PER-HANDLE allowed positions (2026-08-15 user report: "I check the
 * positions, switch handle, come back and my checks are gone") ─────────────
 * The allowed-position checks used ONE flat opts key ("handlePlacements") for
 * the whole product, but placement IDS ARE NOT UNIQUE ACROSS HANDLES — every
 * handle without custom positions uses the same standard trio (pl-top /
 * pl-center / pl-bottom), and authored ones repeat pl-1, pl-2… So handle A's
 * ticks silently became handle B's, and ticking all of B's positions tripped
 * the NONE rule (list → null = unrestricted), which read back as "everything
 * ticked" on A: the checks looked forgotten.
 * Each handle now owns its own list under "handlePlacements:<CODE>". The old
 * flat key is still READ as the fallback, so lists authored before today keep
 * working until that handle is edited again. */
export function handlePlacementCat(handleCode) {
  var c = String(handleCode == null ? "" : handleCode);
  return c ? ("handlePlacements:" + c) : "handlePlacements";
}
/* PRESENCE, not truthiness: once a handle has its own key the technician HAS
 * authored it, and null/[] then mean "every position allowed for this handle"
 * (the NONE rule). Testing truthiness instead made an all-allowed handle fall
 * back to the product-wide legacy list, so ticking its last box looked inert. */
function hasOwnCat(o, cat) { return Object.prototype.hasOwnProperty.call(o, cat); }

/** Is this position allowed for THIS handle on this unit? */
export function placementAllows(u, handleCode, plId) {
  var cat = handlePlacementCat(handleCode);
  var o = prodOpts(u);
  // never authored for this handle → the legacy product-wide list still rules
  if (cat !== "handlePlacements" && !hasOwnCat(o, cat)) {
    return optAllows(u, "handlePlacements", plId);
  }
  return optAllows(u, cat, plId);
}
/** Toggle a position for THIS handle (migrates the legacy flat list on the
 *  first edit, so the technician's existing choices are never lost). */
export function placementToggle(u, handleCode, plId, allIds) {
  var cat = handlePlacementCat(handleCode);
  var o = prodOpts(u);
  if (cat !== "handlePlacements" && !hasOwnCat(o, cat)) {
    var legacy = o.handlePlacements;
    // seed from the legacy list, but only with ids THIS handle actually has
    var seed = (legacy && legacy.length)
      ? legacy.filter(function (id) { return allIds.indexOf(id) >= 0; })
      : [];
    // seed only when it is a REAL restriction for this handle; otherwise start
    // from "all allowed" (the legacy list said nothing about these positions)
    o[cat] = (seed.length && seed.length < allIds.length) ? seed : null;
  }
  optToggle(u, cat, plId, allIds);
}

export function optAllows(u, cat, id) { var l = prodOpts(u)[cat]; return (l == null || !l.length) ? true : (l.indexOf(id) >= 0); }
export function optToggle(u, cat, id, allIds) {
  var o = prodOpts(u);
  // empty == null == "all allowed" (NONE rule), so toggling FROM the None state
  // starts from the full set — clicking a ticked item unticks it, never inverts.
  var cur = (o[cat] == null || !o[cat].length) ? allIds.slice() : o[cat].slice();
  var k = cur.indexOf(id);
  if (k >= 0) cur.splice(k, 1); else cur.push(id);
  o[cat] = (cat !== "lighting" && cur.length >= allIds.length) ? null : cur;
}

export function allCarcIds(blob) { return blob.colors.map(function (c, i) { return i; }); }
export function allGroupIds(blob) { return blob.groups.map(function (g) { return g.g; }); }
export function allGolaIds(blob) { return blob.gola.map(function (c, i) { return i; }); }
export function allPlinthIds(blob) { return blob.pmat.map(function (m, i) { return m.key || i; }); }

export function allowedCarcIdx(blob, u) { var r = []; blob.colors.forEach(function (c, i) { if (optAllows(u, "carcass", i)) r.push(i); }); return r; }
export function allowedGroupIdx(blob, u) { if (!unitHasFront(u)) return []; var r = []; blob.groups.forEach(function (g, i) { if (optAllows(u, "groups", g.g)) r.push(i); }); return r; }
export function allowedGolaIdx(blob, u) { if (!unitHasFront(u)) return []; var r = []; blob.gola.forEach(function (c, i) { if (optAllows(u, "gola", i)) r.push(i); }); return r; }
export function allowedPlinthIdx(blob, u) { if (!unitNeedsPlinth(u)) return []; var r = []; blob.pmat.forEach(function (m, i) { if (optAllows(u, "plinth", m.key || i)) r.push(i); }); return r; }

export function clampSpecToProduct(blob, u, s) {
  if (!u || !s) return s;
  var g = allowedGroupIdx(blob, u); if (s.grp >= 0 && g.indexOf(s.grp) < 0) s.grp = (g.length ? g[0] : -1);
  var ca = allowedCarcIdx(blob, u); if (s.carc >= 0 && ca.indexOf(s.carc) < 0) s.carc = (ca.length ? ca[0] : -1);
  var p = allowedPlinthIdx(blob, u); if (s.plinth >= 0 && p.indexOf(s.plinth) < 0) s.plinth = (p.length ? p[0] : -1);
  // every component family: a pick the technician has since disallowed (or
  // that no longer applies to this unit type) drops back to None — never
  // silently prices something the product does not offer.
  MODULE_COMPONENTS.forEach(function (c) {
    if (s[c.field] == null || s[c.field] < 0) return;
    if (!osOptApplies(u, c.cat) || compAllowedIdx(blob, u, c.cat).indexOf(s[c.field]) < 0) s[c.field] = -1;
  });
  EQUIPMENT_TYPES.forEach(function (t) {
    if (s[t.field] == null || s[t.field] < 0) return;
    var list = ((blob.equipment || {})[t.cat]) || [];
    if (!optAllows(u, "equipment", t.cat) || s[t.field] >= list.length) s[t.field] = -1;
  });
  if (s.lightType && s.lightType !== "none") {
    if (u.lightCap === false || lightingTypesFor(u).indexOf(s.lightType) < 0 || !optAllows(u, "lighting", s.lightType)) {
      s.lightType = "none"; s.lightSensor = false;
    }
  }
  return s;
}

const EQ_OPT_CATS = ["hinges", "drawers", "shelves", "organizers", "bins", "corners", "pullouts", "columns"];

export function optsetItems(blob, cat, u) {
  if (cat === "carcass") return blob.colors.map(function (c, i) { return { id: i, label: c.name }; });
  if (cat === "groups") return blob.groups.map(function (g) { return { id: g.g, label: "PG" + g.g + " " + (g.name || "") }; });
  if (cat === "gola") return blob.gola.map(function (c, i) { return { id: i, label: c.name }; });
  if (cat === "plinth") return blob.pmat.map(function (m, i) { return { id: (m.key || i), label: m.name }; });
  if (cat === "lighting") {
    var ltn = { drawer: "Drawer", vertical: "Vertical", horizontal: "Horizontal" };
    return ["drawer", "vertical", "horizontal"].map(function (t) { return { id: t, label: ltn[t] }; });
  }
  // batch 14: one allow-list column per Fabric Library zone, each listing ITS groups (the
  // configurator gates the indoor zone by "fabric", leather by "leather", … engine/fabrics)
  if (cat === "fabric") return (((blob.fabrics || {}).indoor) || []).map(function (f) { return { id: f.key, label: f.label }; });
  if (cat === "leather") return (((blob.fabrics || {}).leather) || []).map(function (f) { return { id: f.key, label: f.label }; });
  if (cat === "outfabric") return (((blob.fabrics || {}).outdoor) || []).map(function (f) { return { id: f.key, label: f.label }; });
  if (cat === "rope") return (((blob.fabrics || {}).rope) || []).map(function (f) { return { id: f.key, label: f.label }; });
  // ── v4.56 additions (HTML 57943+) ──
  if (cat === "handles") return (blob.handles || []).map(function (c) { return { id: c.code, label: c.name || c.code }; });
  if (EQ_OPT_CATS.indexOf(cat) >= 0) return (((blob.equipment || {})[cat]) || []).map(function (it) { return { id: it.code, label: (it.brand ? it.brand + " " : "") + (it.model || it.code) }; });
  if (cat === "sidefront") return (blob.groups || []).map(function (g) { return { id: g.g, label: "PG" + g.g + " " + (g.name || "") }; });
  if (cat === "sideworktop" || cat === "worktops") return (blob.worktops || []).map(function (m) { return { id: m.name, label: m.name }; });
  if (cat === "backsplash") return (blob.backsplash || []).map(function (m) { return { id: m.name, label: m.name }; });
  if (cat === "equipment") {
    return [["sinks", "Sinks"], ["taps", "Taps"], ["hobs", "Hobs"], ["ovens", "Ovens"], ["hoods", "Hoods"], ["fridges", "Fridges"], ["dishwashers", "Dishwashers"]]
      .map(function (o) { return { id: o[0], label: o[1] }; });
  }
  return [];
}
export function optsetAllIds(blob, cat, u) { return optsetItems(blob, cat, u).map(function (it) { return it.id; }); }

/* ── v4.56 component selects (HTML compLabels/compAllowedIdx) ────────────── */
export function compLabels(blob, cat) {
  if (cat === "sidefront") return (blob.groups || []).map(function (g) { return "PG" + g.g + " " + (g.name || ""); });
  if (cat === "sideworktop" || cat === "worktops") return (blob.worktops || []).map(function (m) { return m.name; });
  if (cat === "handles") return (blob.handles || []).map(function (it) { return it.name || it.code || ""; });
  if (cat === "backsplash") return (blob.backsplash || []).map(function (m) { return m.name; });
  return (((blob.equipment || {})[cat]) || []).map(function (it) { return (it.brand ? it.brand + " " : "") + (it.model || it.code || ""); });
}
export function compAllowedIdx(blob, u, cat) {
  var arr, idf;
  if (cat === "sidefront") { arr = blob.groups || []; idf = function (it) { return it.g; }; }
  else if (cat === "sideworktop" || cat === "worktops") { arr = blob.worktops || []; idf = function (it) { return it.name; }; }
  else if (cat === "handles") { arr = blob.handles || []; idf = function (it) { return it.code; }; }
  else if (cat === "backsplash") { arr = blob.backsplash || []; idf = function (it) { return it.name; }; }
  else { arr = ((blob.equipment || {})[cat]) || []; idf = function (it) { return it.code; }; }
  var out = [];
  arr.forEach(function (it, k) { if (optAllows(u, cat, idf(it))) out.push(k); });
  return out;
}

/* ── v4.56 applicability gating (HTML osOptApplies) — drives the "—" cells ─ */
export function osOptApplies(u, cat) {
  if (cat === "groups" || cat === "gola" || cat === "handles" || cat === "hinges") return unitHasFront(u);
  if (cat === "plinth") return unitNeedsPlinth(u);
  if (cat === "drawers" || cat === "organizers") return unitHasDrawers(u);
  if (cat === "corners") return /corner/i.test(u.cat || "");
  if (cat === "columns") return unitIsTall(u);
  if (cat === "bins" || cat === "pullouts") return unitNeedsPlinth(u) && !unitIsTall(u);
  return true;
}

/* ── v4.56 Wizard (room level) → cabinet config inheritance (HTML 57842+).
 *    kw = the kitchenWizard state ({shell,front,gola,plinth,handle} string
 *    ids as produced by the LHCMat/room-bridge id scheme). Values that don't
 *    resolve are SKIPPED — the cabinet keeps the product default. ─────────── */
export function kwCarcIdx(blob, kw) { return _uidResolveIdx(blob.colors, (kw && kw.shell) || "", "carc"); }
export function kwGrpIdx(blob, kw) { var m = /^pg(\d+)$/.exec(String((kw && kw.front) || "")); if (!m) return -1; var g = +m[1]; for (var i = 0; i < blob.groups.length; i++) { if (blob.groups[i] && +blob.groups[i].g === g) return i; } return -1; }
export function kwGolaIdx(blob, kw) { return _uidResolveIdx(blob.gola, (kw && kw.gola) || "", "gola"); }
export function kwPlinthIdx(blob, kw) {
  var key = String((kw && kw.plinth) || ""); if (!key) return -1;
  for (var i = 0; i < blob.pmat.length; i++) { var m = blob.pmat[i]; if (m && m.key === key) return i; }
  var uk = _uidResolveIdx(blob.pmat, key, "plinth"); if (uk >= 0) return uk;
  return -1;
}
export function kwHandleIdx(blob, kw) { var code = String((kw && kw.handle) || ""); if (!code) return -1; for (var i = 0; i < blob.handles.length; i++) { if (blob.handles[i] && blob.handles[i].code === code) return i; } return -1; }
export function inheritRoomDefaults(blob, u, c, kw) {
  try {
    if (!kw || !c) return;
    var ci, gi, gg, pi, hi;
    if (c.carc === -1 && (ci = kwCarcIdx(blob, kw)) >= 0) c.carc = ci;
    if (c.grp === -1 && (gi = kwGrpIdx(blob, kw)) >= 0) c.grp = gi;
    if (c.gola === -1 && (gg = kwGolaIdx(blob, kw)) >= 0) c.gola = gg;
    if (c.plinth === -1 && (pi = kwPlinthIdx(blob, kw)) >= 0) c.plinth = pi;
    if (c.handle === -1 && (hi = kwHandleIdx(blob, kw)) >= 0) c.handle = hi;
    // Opening system: wizard "packing" is assembly, not opening — leave sys
    // alone unless a gola was inherited (implies a handleless/gola system).
    if ((c.sys === "none" || c.sys == null) && c.gola >= 0) c.sys = "gola";
    // Never let an inherited value violate the product's allow-list.
    clampSpecToProduct(blob, u, c);
  } catch (e) { /* inheritance is best-effort by contract */ }
}

/* ── door colour resolution (prototype 56479-56480) ─────────────────────── */
export function groupDoors(blob, grpIdx) { var g = blob.groups[grpIdx] || blob.groups[0]; return (g && g.doors) || []; }
export function doorColourObj(blob, grpIdx, colIdx) { var ds = groupDoors(blob, grpIdx); return ds[colIdx] || ds[0] || { name: "—" }; }

/* ── THE shared pricing engine (prototype 56482-56501) ──────────────────── */
/* ── PER-COMPONENT PRICE LINES (2026-07-31) — every configured component
 * contributes its OWN line, priced from its own library entry. Nothing is
 * hardcoded: equipment-library items price at their catalogued € price; the
 * side covers price through the SAME rate semantics their library declares
 * (front groups are €/m², worktop materials €/m) against the unit's real side
 * face, and the label shows the arithmetic so the technician can audit it.
 * A component with no price set simply adds no line (never a guessed value). */
export function componentPriceLines(blob, unit, spec) {
  var out = [];
  if (!blob || !unit || !spec) return out;
  var d = cfgDimsM(unit) || { width: 0, height: 0, depth: 0 };
  MODULE_COMPONENTS.forEach(function (c) {
    var idx = spec[c.field];
    if (idx == null || idx < 0) return;
    if (!osOptApplies(unit, c.cat)) return;
    if (c.cat === "handles") return;                    // already priced by the handle/gola system block
    if (c.cat === "sidefront") {
      var gsel = (blob.groups || [])[idx];
      if (!gsel || !(gsel.price > 0)) return;
      var area = Math.round(d.height * d.depth * 1000) / 1000;   // side face m²
      if (!(area > 0)) return;
      out.push({ k: "sidefront", label: "Side cover · front · " + (gsel.name || ("PG" + gsel.g)) + " · " + area + " m² × €" + gsel.price + "/m²",
        val: Math.round(area * gsel.price * 100) / 100 });
      return;
    }
    if (c.cat === "sideworktop") {
      var wsel = (blob.worktops || [])[idx];
      if (!wsel || !(wsel.price > 0)) return;
      var runM = Math.round(d.depth * 1000) / 1000;              // side edge run m
      if (!(runM > 0)) return;
      out.push({ k: "sideworktop", label: "Side cover · worktop · " + (wsel.name || "") + " · " + runM + " m × €" + wsel.price + "/m",
        val: Math.round(runM * wsel.price * 100) / 100 });
      return;
    }
    var item = (((blob.equipment || {})[c.cat]) || [])[idx];
    if (!item || !(item.price > 0)) return;
    var nm = (item.brand ? item.brand + " " : "") + (item.model || item.code || "");
    out.push({ k: c.cat, label: c.label + " · " + nm, val: Math.round(item.price * 100) / 100 });
  });
  EQUIPMENT_TYPES.forEach(function (t) {
    var idx = spec[t.field];
    if (idx == null || idx < 0) return;
    if (!optAllows(unit, "equipment", t.cat)) return;
    var it = (((blob.equipment || {})[t.cat]) || [])[idx];
    if (!it || !(it.price > 0)) return;
    var label = (it.brand ? it.brand + " " : "") + (it.model || it.code || "");
    out.push({ k: t.cat, label: t.label.replace(/s$/, "") + " · " + label, val: Math.round(it.price * 100) / 100 });
  });
  return out;
}

export function priceCabinet(blob, unit, spec) {
  if (!unit) return { lines: [], total: 0 };
  var lines = [], w = unitWidthCm(unit) || 0;
  lines.push({ k: "carcass", label: "Carcass · " + unit.sku, val: (unit.p1 != null ? unit.p1 : (unit.price != null ? unit.price : 0)) });
  var grpObj = (spec.grp != null && spec.grp >= 0) ? (blob.groups[spec.grp] || null) : null;
  // QUIRK KEPT: the door COLOUR object is passed as the style arg, so its
  // (nonexistent) addM2 is 0 — DOOR_STYLES surcharge never applies here.
  var dc = grpObj ? computeDoorCost(unit, grpObj, doorColourObj(blob, spec.grp, spec.colour)) : null;
  if (dc) lines.push({ k: "door", label: "Door front · " + dc.group.name + " · " + dc.style.name + (dc.oversize ? " (+OS)" : ""), val: dc.cost });
  if (dc && grpObj && grpObj.edgePrice > 0) {
    var efd = unitFaceDims(unit);
    if (efd) {
      var edgeM = Math.round(2 * (efd.w + efd.h) / 100 * 1000) / 1000;
      var edgeCost = Math.round(edgeM * grpObj.edgePrice * 100) / 100;
      lines.push({ k: "edge", label: "Edge banding · " + edgeM + "m × €" + grpObj.edgePrice + "/m", val: edgeCost });
    }
  }
  if (spec.sys === "handle") {
    if (spec.handle != null && spec.handle >= 0) {
      var h = blob.handles[spec.handle];
      if (h) lines.push({ k: "handle", label: "Handle · " + h.name, val: (h.price || 0) });
    }
  } else if (spec.sys === "gola") {
    if (spec.gola != null && spec.gola >= 0) {
      var gc = blob.gola[spec.gola];
      var gcc = computeGolaCost(unit, gc);
      if (gcc) lines.push({ k: "gola", label: "Gola · " + gc.name + (gcc.cost ? "" : " (incl.)"), val: gcc.cost });
    }
  }
  var plM = (spec.plinth != null && spec.plinth >= 0) ? (blob.pmat[spec.plinth] || null) : null;
  var pc = plM ? computePlinthCost(unit, plM, grpObj ? grpObj.price : null) : null;
  if (pc) lines.push({ k: "plinth", label: "Plinth · " + pc.mat.name + (plM && plM.key === "door" && grpObj ? " · " + grpObj.name : ""), val: pc.cost });
  if (unitHasWorktop(unit) && spec.counter != null && spec.counter >= 0) {
    var ct = blob.countertops[spec.counter];
    if (ct) lines.push({ k: "countertop", label: "Countertop · " + ct.name, val: Math.round((w / 100) * ct.price * 100) / 100 });
  }
  if (unitHasSink(unit) && spec.sink != null && spec.sink >= 0) {
    var sk = ((blob.equipment && blob.equipment.sinks) || [])[spec.sink];
    if (sk && sk.price != null) lines.push({ k: "sink", label: "Sink · " + (sk.brand || "") + " " + (sk.model || sk.code || ""), val: sk.price });
  }
  if (spec.appl >= 0) {
    var ap = blob.appliances[spec.appl];
    if (ap && ap.price != null) lines.push({ k: "appliance", label: "Appliance · " + ap.brand + " " + ap.name, val: ap.price });
  }
  if (spec.lightType && spec.lightType !== "none" && unit.lightCap !== false && optAllows(unit, "lighting", spec.lightType) && lightingTypesFor(unit).indexOf(spec.lightType) >= 0) {
    var lcst = computeLightingCost(blob, unit, spec.lightType, !!spec.lightSensor);
    if (lcst && lcst.cost) lines.push({ k: "lighting", label: lcst.label, val: lcst.cost });
  }
  componentPriceLines(blob, unit, spec).forEach(function (l) { lines.push(l); });
  var total = lines.reduce(function (s, l) { return s + (l.val || 0); }, 0);
  return { lines: lines, total: Math.round(total * 100) / 100 };
}

export function rowPrice(blob, u) { var c = cfgOf(u); var pr = priceCabinet(blob, u, c); return Math.round(pr.total * 100) / 100; }

/* ── Fronts library mirror for the "door" plinth material (prototype 53800) */
export function frontsByGroup(blob) {
  var out = [];
  try {
    blob.groups.forEach(function (g) {
      out.push({
        g: g.g, name: g.name || ("PG" + g.g), price: (g.price || 0),
        colors: (g.doors || []).map(function (d) { return { name: d.name || "Front", sw: d.sw || "#9A9BA0" }; }),
      });
    });
  } catch (e) { /* tolerate malformed groups */ }
  return out;
}

/* ── BENCH ↔ CUSTOMER PRICE PARITY (2026-08-06, usability-audit A7/A8) ─────
 * The bench's own priceCabinet formulas diverged from what customers pay
 * (carcass/front deltas missing, handle/gola gated on an unsettable sys,
 * per-metre worktop/plinth vs the engine's flat deltas). benchPrice maps the
 * technician cfg vocabulary onto a REAL placement config and prices it with
 * THE engine (configOptionDeltas) — one pricing truth, bench == quote. */
export function benchConfigOf(blob, unit, spec) {
  var cfg = {};
  if (spec.carc >= 0 && (blob.colors || [])[spec.carc]) cfg.carcass = "carc-" + _uidTokFor(blob.colors[spec.carc], spec.carc);
  if (spec.grp >= 0 && (blob.groups || [])[spec.grp]) {
    var g = blob.groups[spec.grp];
    cfg.door = "pg" + g.g;
    if (spec.colour >= 0 && (g.doors || [])[spec.colour]) cfg.doorColor = "pg" + g.g + "-c" + _uidTokFor(g.doors[spec.colour], spec.colour);
  }
  if (spec.doorOpening === "right") cfg.hingeDir = "Right";
  else if (spec.doorOpening === "left") cfg.hingeDir = "Left";
  if (spec.sys === "handle" && spec.handle >= 0 && (blob.handles || [])[spec.handle]) {
    cfg.handle = blob.handles[spec.handle].code;
    // per-front default positions authored on the bench (2026-08-11)
    if (spec.handlePlacements && typeof spec.handlePlacements === "object" && Object.keys(spec.handlePlacements).length) {
      cfg.handlePlacements = spec.handlePlacements;
    }
  }
  if (spec.sys === "gola" && spec.gola >= 0 && (blob.gola || [])[spec.gola]) {
    cfg.gola = "gola-" + _uidTokFor(blob.gola[spec.gola], spec.gola);
    // BENCH GOLA VISIBLE (2026-08-21 user: "I wanted to test it in Cabinet
    // Technician and the gola isn't being put there"): the engine builds the
    // vertical profile only when a PLACEMENT exists — the bench never wrote
    // one, so the canonical stage showed nothing.
    // 2026-08-26: the side is now the technician's pick, "left right or left +
    // right like in configurator soo u can put gola where u need to test it".
    // Anything unrecognised (including no pick at all) stays Left, which is
    // what the bench has always shown; the engine itself gates by role.
    cfg.golaPos = (spec.golaPos === "R" || spec.golaPos === "LR") ? spec.golaPos : "L";
  }
  if (spec.backsplash >= 0 && (blob.backsplash || [])[spec.backsplash]) cfg.backsplash = blob.backsplash[spec.backsplash].name;
  if (spec.drawer >= 0 && (((blob.equipment || {}).drawers) || [])[spec.drawer]) cfg.drawerRunner = blob.equipment.drawers[spec.drawer].code;
  var interior = [];
  [["shelf", "shelves"], ["organizer", "organizers"], ["bin", "bins"], ["corner", "corners"], ["pullout", "pullouts"], ["column", "columns"]].forEach(function (pair) {
    var k = spec[pair[0]];
    var it = k >= 0 ? ((((blob.equipment || {})[pair[1]]) || [])[k]) : null;
    if (it && it.code) interior.push({ cat: pair[1], code: it.code, qty: 1 });
  });
  if (interior.length) cfg.interior = interior;
  if (spec.plinth >= 0 && (blob.pmat || [])[spec.plinth]) {
    var pm = blob.pmat[spec.plinth];
    cfg.plinth = { material: pm.key || ("plinth-" + _uidTokFor(pm, spec.plinth)) };
  }
  if (spec.worktop >= 0 && (blob.worktops || [])[spec.worktop]) cfg.countertop = blob.worktops[spec.worktop].name;
  if (spec.lightType && spec.lightType !== "none") cfg.lighting = { type: spec.lightType };
  return cfg;
}

export function benchPrice(blob, unit, spec) {
  var d = cfgDimsM(unit);
  var base = (unit.p1 != null ? unit.p1 : (unit.price != null ? unit.price : 0));
  var lines = [{ k: "carcass", label: "Carcass · " + unit.sku, val: base }];
  try {
    // lazy require avoids pulling the whole engine chunk into every catalog
    // view — only the bench pays for it (and jest resolves it relatively).
    var eng = require("../../configurator/engine/pricing");
    var def = { width: d.width, height: d.height, depth: d.depth, frontLayouts: unit.frontLayouts || null, model3d: (unit.model3d || null) };
    eng.configOptionDeltas(blob, def, benchConfigOf(blob, unit, spec)).forEach(function (l) {
      lines.push({ k: l.key, label: l.label, val: l.val });
    });
  } catch (e) { /* engine unavailable → base price only, never a crash */ }
  var total = lines.reduce(function (s, l) { return s + (l.val || 0); }, 0);
  return { lines: lines, total: Math.round(total * 100) / 100 };
}
